/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faStar, faEnvelope, faChevronLeft, faChevronRight, faHome, faExpand, faImages } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import "./cardpopup.css";
import { getPropertyImageUrl } from "../../../../utils/property-image";
import { useLanguage } from "../../../../i18n/LanguageContext";

function CardPopup() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { propertyId } = useParams();

  const getAmenityLabel = (amenity) => {
    const id = typeof amenity === 'string' ? amenity.toLowerCase().replace(/\s/g, '') : amenity;
    const translated = t(`amenities.${id}`);
    return translated !== `amenities.${id}` ? translated : amenity;
  };
  const [property, setProperty] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = React.useState(0);
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const [panX, setPanX] = React.useState(0);
  const [panY, setPanY] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  // Rental modal state removed — renting now goes through the application flow
  const [showMessageModal, setShowMessageModal] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [sendingMessage, setSendingMessage] = React.useState(false);
  const [reviews, setReviews] = React.useState([]);
  const [canReview, setCanReview] = React.useState(false);
  const [userRentals, setUserRentals] = React.useState([]);
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewRating, setReviewRating] = React.useState(5);
  const [reviewText, setReviewText] = React.useState("");
  const [selectedRentalId, setSelectedRentalId] = React.useState(null);
  const [submittingReview, setSubmittingReview] = React.useState(false);
  const lightboxImageRef = React.useRef(null);
  const [slideDir, setSlideDir] = React.useState("right");
  const [touchStartX, setTouchStartX] = React.useState(null);

  const userRole = localStorage.getItem("role");

  React.useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await fetch(
          `/api/properties/${propertyId}`
        );
        if (!response.ok) {
          throw new Error("Property not found");
        }
        const data = await response.json();
        setProperty(data);
      } catch (error) {
        console.error("Error fetching property:", error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await fetch(
          `/api/reviews/property/${propertyId}`
        );
        if (response.ok) {
          const data = await response.json();
          setReviews(data);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    const checkCanReview = async () => {
      const token = localStorage.getItem("token");
      if (!token || userRole !== "renter") return;

      try {
        const response = await fetch(
          `/api/reviews/can-review/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setCanReview(data.canReview);
          setUserRentals(data.rentals);
          if (data.rentals.length > 0) {
            setSelectedRentalId(data.rentals[0].rental_id);
          }
        }
      } catch (error) {
        console.error("Error checking review eligibility:", error);
      }
    };

    if (propertyId) {
      fetchProperty();
      fetchReviews();
      checkCanReview();
    }
  }, [propertyId, userRole]);

  // Listener lifecycle is controlled by lightbox visibility.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (lightboxOpen) {
      window.addEventListener("keydown", handleLightboxKeyDown);
      window.addEventListener("wheel", handleLightboxWheel, { passive: false });
      window.addEventListener("mousemove", handleLightboxMouseMove);
      window.addEventListener("mouseup", handleLightboxMouseUp);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", handleLightboxKeyDown);
        window.removeEventListener("wheel", handleLightboxWheel);
        window.removeEventListener("mousemove", handleLightboxMouseMove);
        window.removeEventListener("mouseup", handleLightboxMouseUp);
        document.body.style.overflow = "unset";
      };
    }
  }, [lightboxOpen, isDragging, dragStart, zoomLevel, panX, panY]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    navigate(-1);
  };

  const handlePrevImage = () => {
    if (property?.photos && property.photos.length > 0) {
      setSlideDir("left");
      setCurrentImageIndex((prev) =>
        prev === 0 ? property.photos.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (property?.photos && property.photos.length > 0) {
      setSlideDir("right");
      setCurrentImageIndex((prev) =>
        prev === property.photos.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleGalleryTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleGalleryTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNextImage();
      else handlePrevImage();
    }
    setTouchStartX(null);
  };

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setLightboxOpen(true);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const handleLightboxWheel = (e) => {
    if (!lightboxOpen) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(1, Math.min(5, zoomLevel + delta));
    setZoomLevel(newZoom);
    
    if (delta < 0) {
      setPanX(0);
      setPanY(0);
    }
  };

  const handleLightboxMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleLightboxMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    const newPanX = e.clientX - dragStart.x;
    const newPanY = e.clientY - dragStart.y;
    
    setPanX(newPanX);
    setPanY(newPanY);
  };

  const handleLightboxMouseUp = () => {
    setIsDragging(false);
  };

  const handleLightboxKeyDown = (e) => {
    if (!lightboxOpen) return;
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowLeft") prevLightboxImage();
    if (e.key === "ArrowRight") nextLightboxImage();
  };

  const prevLightboxImage = () => {
    setLightboxImageIndex((prev) =>
      prev === 0 ? (property?.photos?.length || 1) - 1 : prev - 1
    );
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const nextLightboxImage = () => {
    setLightboxImageIndex((prev) =>
      prev === (property?.photos?.length || 1) - 1 ? 0 : prev + 1
    );
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const getTypeLabel = (type) => {
    if (!type) return "";
    const normalized = String(type).trim().toLowerCase().replace(/\s+/g, " ");
    const key = normalized === "office space" ? "officeSpace" : normalized.replace(/\s+/g, "");
    const translated = t(`findProperty.categories.${key}`);
    return translated === `findProperty.categories.${key}` ? capitalize(normalized) : translated;
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case "house":
        return "#007bff";
      case "apartment":
        return "#28a745";
      case "land":
        return "#ffc107";
      case "commercial":
        return "#fd7e14";
      case "condo":
        return "#6f42c1";
      case "cabin":
        return "#8B4513";
      case "room":
        return "#ff6b6b";
      default:
        return "#6c757d";
    }
  };

  const detailHighlights = [
    `${property?.bedrooms || 0} bedroom${property?.bedrooms === 1 ? "" : "s"}`,
    `${property?.bathrooms || 0} bathroom${property?.bathrooms === 1 ? "" : "s"}`,
    property?.sqft ? `${property.sqft} sq ft space` : "Flexible floor space",
    property?.amenities?.length
      ? `${property.amenities.length} amenities included`
      : "Essential amenities supported",
  ];

  const handleRentNow = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please login to apply for a property");
      navigate("/login");
      return;
    }
    if (userRole !== "renter") {
      alert("Only renters can apply for properties. Please create a renter account.");
      return;
    }
    navigate(`/apply/${propertyId}`);
  };

  const handleContactOwner = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please login to contact the owner");
      navigate("/login");
      return;
    }
    
    setShowMessageModal(true);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      alert("Please enter a message");
      return;
    }

    setSendingMessage(true);
    const token = localStorage.getItem("token");

    try {
      // Get property owner ID from the property data
      // We need to fetch the full property data to get the owner ID
      const propResponse = await fetch(`/api/properties/${propertyId}`);
      const propData = await propResponse.json();

      const response = await fetch("/api/inquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          propertyId: propertyId,
          receiverId: propData.user_id || propData.ownerId,
          message: message
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert("Message sent successfully!");
        setShowMessageModal(false);
        setMessage("");
      } else {
        alert(data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Error connecting to server");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedRentalId) {
      alert("Please select a rental");
      return;
    }

    if (!reviewText.trim()) {
      alert("Please write a review");
      return;
    }

    setSubmittingReview(true);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch("/api/reviews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          propertyId: propertyId,
          rentalId: selectedRentalId,
          rating: reviewRating,
          reviewText: reviewText
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert("Review submitted successfully!");
        setShowReviewModal(false);
        setReviewText("");
        setReviewRating(5);
        
        // Refresh reviews and check review status
        const reviewsResponse = await fetch(
          `/api/reviews/property/${propertyId}`
        );
        if (reviewsResponse.ok) {
          const reviewsData = await reviewsResponse.json();
          setReviews(reviewsData);
        }

        const canReviewResponse = await fetch(
          `/api/reviews/can-review/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (canReviewResponse.ok) {
          const canReviewData = await canReviewResponse.json();
          setCanReview(canReviewData.canReview);
          setUserRentals(canReviewData.rentals);
        }

        // Refresh property to get updated rating
        const propResponse = await fetch(
          `/api/properties/${propertyId}`
        );
        if (propResponse.ok) {
          const propData = await propResponse.json();
          setProperty(propData);
        }
      } else {
        alert(data.message || "Failed to submit review");
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Error connecting to server");
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, size = "1x") => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <FontAwesomeIcon
            key={i}
            icon={faStar}
            className="text-warning"
            size={size}
          />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <FontAwesomeIcon
            key={i}
            icon={faStar}
            className="text-warning"
            style={{ opacity: 0.5 }}
            size={size}
          />
        );
      } else {
        stars.push(
          <FontAwesomeIcon
            key={i}
            icon={faStarRegular}
            className="text-warning"
            size={size}
          />
        );
      }
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="property-page-shell">
        <div className="property-page-container">
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="property-page-shell">
        <div className="property-page-container">
          <button className="property-page-back" onClick={handleClose}>
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            Back to results
          </button>
          <div className="text-center p-5 text-muted">
            <p>Property not found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="property-page-shell">
      <div className="property-page-container">
        <button className="property-page-back" onClick={handleClose}>
          <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
          Back to results
        </button>

        <div className="property-page-grid">
          <div className="property-main-column">
            <div
              className="popup-gallery"
              onTouchStart={handleGalleryTouchStart}
              onTouchEnd={handleGalleryTouchEnd}
            >
              <div
                className="popup-gallery-main"
                onClick={() => openLightbox(currentImageIndex)}
                title="Click to view fullscreen"
              >
                <img
                  key={currentImageIndex}
                  src={
                    property.photos?.[currentImageIndex]
                      ? getPropertyImageUrl(property.photos[currentImageIndex], {
                          width: 1600,
                          quality: 95,
                          format: "jpeg",
                        })
                      : "https://via.placeholder.com/1200x600?text=No+Image"
                  }
                  alt={`${property.name || "Property"} - ${currentImageIndex + 1}`}
                  className={`popup-gallery-img slide-${slideDir}`}
                  draggable={false}
                />
                <div className="popup-gallery-gradient" />

                <span
                  className="badge popup-gallery-type-tag"
                  style={{ backgroundColor: getTypeColor(property.type), color: "#fff" }}
                >
                  {getTypeLabel(property.type)}
                </span>

                {property.photos?.length > 0 && (
                  <div className="popup-gallery-counter">
                    <FontAwesomeIcon icon={faImages} className="me-1" />
                    {currentImageIndex + 1} / {property.photos.length}
                  </div>
                )}

                <button
                  className="popup-gallery-expand"
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox(currentImageIndex);
                  }}
                  aria-label="View fullscreen"
                  title="Fullscreen"
                >
                  <FontAwesomeIcon icon={faExpand} />
                </button>

                {property.photos && property.photos.length > 1 && (
                  <>
                    <button
                      className="popup-gallery-nav popup-gallery-nav-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      aria-label="Previous image"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className="popup-gallery-nav popup-gallery-nav-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      aria-label="Next image"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </>
                )}
              </div>

              {property.photos && property.photos.length > 1 && (
                <div className="popup-gallery-thumbs">
                  {property.photos.map((photo, index) => (
                    <button
                      key={index}
                      className={`popup-gallery-thumb${index === currentImageIndex ? " active" : ""}`}
                      onClick={() => {
                        setSlideDir(index > currentImageIndex ? "right" : "left");
                        setCurrentImageIndex(index);
                      }}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img
                        src={getPropertyImageUrl(photo, { width: 220, quality: 93, format: "webp" })}
                        alt={`Thumbnail ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="popup-details">
              <h2 className="fw-bold mb-2">{property.name || "Untitled Property"}</h2>
              <p className="text-muted mb-3">
                {property.state}, {property.city}
              </p>

              <div className="property-meta-chips mb-4">
                <span className="property-meta-chip">{getTypeLabel(property.type)}</span>
                <span className="property-meta-chip">{property.city || "City"}</span>
                <span className="property-meta-chip">{property.state || "Province"}</span>
                <span className="property-meta-chip property-meta-chip-accent">Available now</span>
              </div>

              <div className="row mb-4">
                <div className="col-md-6">
                  <h4 className="property-price fw-semibold mb-2">
                    NPR {property.price?.toLocaleString() || "N/A"}
                  </h4>
                  <p className="text-muted small">per month</p>
                </div>
                <div className="col-md-6 text-end">
                  <div className="rating text-warning">
                    {renderStars(
                      reviews.length > 0
                        ? reviews.reduce((sum, r) => sum + Number(r.rating), 0) / reviews.length
                        : (property.average_rating || 0)
                    )}
                    <span className="ms-2 text-muted small">
                      ({reviews.length || property.total_reviews || 0} review{(reviews.length || property.total_reviews) !== 1 ? "s" : ""})
                    </span>
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-4">
                  <span className="fs-6 fw-normal">
                    <strong>{property.bedrooms || 0}</strong> Bedrooms
                  </span>
                </div>
                <div className="col-4">
                  <span className="fs-6 fw-normal">
                    <strong>{property.bathrooms || 0}</strong> Bathrooms
                  </span>
                </div>
                <div className="col-4">
                  <span className="fs-6 fw-normal">
                    <strong>{property.sqft || "N/A"}</strong> sq ft
                  </span>
                </div>
              </div>

              <div className="property-insight-grid mb-4">
                <div className="property-insight-card">
                  <div className="property-insight-label">Total monthly</div>
                  <div className="property-insight-value property-price">NPR {property.price?.toLocaleString() || "N/A"}</div>
                </div>
                <div className="property-insight-card">
                  <div className="property-insight-label">Listing status</div>
                  <div className="property-insight-value">Open for applications</div>
                </div>
                <div className="property-insight-card">
                  <div className="property-insight-label">Response confidence</div>
                  <div className="property-insight-value">Fast owner contact flow</div>
                </div>
              </div>

              <div className="property-highlight-panel mb-4">
                <h5 className="fw-semibold mb-2">Why this home stands out</h5>
                <ul className="property-highlight-list mb-0">
                  {detailHighlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              {property.description && (
                <div className="mb-4">
                  <h5 className="fw-semibold mb-2">Description</h5>
                  <p className="text-muted">{property.description}</p>
                </div>
              )}

              {property.amenities && property.amenities.length > 0 && (
                <div className="mb-4">
                  <h5 className="fw-semibold mb-2">{t("amenities.sectionTitle")}</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {property.amenities.map((amenity, index) => (
                      <span key={index} className="badge bg-light text-dark">
                        {getAmenityLabel(amenity)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Location Map ── */}
              {property.lat != null && property.lng != null && process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                <div className="mb-4">
                  <h5 className="fw-semibold mb-2">Location</h5>
                  <div
                    style={{
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: "1px solid #dee2e6",
                      height: "300px",
                    }}
                  >
                    <iframe
                      title="Property Location"
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${property.lat},${property.lng}&zoom=16`}
                    />
                  </div>
                  {property.address && (
                    <p className="text-muted small mt-2">
                      &#128204; {property.address}
                    </p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold mb-0">Reviews & Ratings</h5>
                  {canReview && userRole === "renter" && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setShowReviewModal(true)}>
                      <FontAwesomeIcon icon={faStar} className="me-2" />
                      Write a Review
                    </button>
                  )}
                </div>

                {property.total_reviews > 0 ? (
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center gap-3">
                      <div className="text-center">
                        <h2 className="mb-0 fw-bold">{property.average_rating?.toFixed(1) || "0.0"}</h2>
                        <div className="text-warning">{renderStars(property.average_rating || 0)}</div>
                        <small className="text-muted">
                          {property.total_reviews} review{property.total_reviews !== 1 ? "s" : ""}
                        </small>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-4 bg-light rounded mb-3">
                    <FontAwesomeIcon icon={faStarRegular} size="2x" className="text-muted mb-2" />
                    <p className="text-muted mb-0">No reviews yet. Be the first to review!</p>
                  </div>
                )}

                {reviews.length > 0 && (
                  <div className="reviews-list">
                    <h6 className="fw-semibold mb-3">Recent Reviews</h6>
                    {reviews.slice(0, 5).map((review) => (
                      <div key={review.id} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="fw-semibold">{review.reviewer_name}</div>
                            <div className="text-warning small">{renderStars(review.rating, "sm")}</div>
                          </div>
                          <small className="text-muted">{new Date(review.created_at).toLocaleDateString()}</small>
                        </div>
                        {review.review_text && <p className="text-muted mb-0 small">{review.review_text}</p>}
                      </div>
                    ))}
                    {reviews.length > 5 && (
                      <p className="text-center text-muted small mb-0">
                        +{reviews.length - 5} more review{reviews.length - 5 !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="property-side-column">
            <div className="property-cta-card">
              <button
                type="button"
                className="property-cta-top property-cta-top-trigger"
                onClick={handleRentNow}
              >
                <p className="property-cta-kicker mb-1">Ready to move in?</p>
                <h3 className="property-cta-price mb-0 property-price">NPR {property.price?.toLocaleString() || "N/A"}</h3>
                <p className="small mb-0">per month</p>
              </button>

              <div className="property-cta-metrics">
                <div>
                  <strong>{property.bedrooms || 0}</strong>
                  <span> Beds</span>
                </div>
                <div>
                  <strong>{property.bathrooms || 0}</strong>
                  <span> Baths</span>
                </div>
                <div>
                  <strong>{property.sqft || "N/A"}</strong>
                  <span> sq ft</span>
                </div>
              </div>

              <div className={`property-cta-actions ${userRole === "renter" ? "" : "single"}`.trim()}>
                {userRole === "renter" && (
                  <button className="btn btn-primary property-cta-btn" onClick={handleRentNow}>
                    <FontAwesomeIcon icon={faHome} className="me-2" />
                    Apply to Rent
                  </button>
                )}
                <button className="btn btn-outline-primary property-cta-btn" onClick={handleContactOwner}>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Contact Owner
                </button>
              </div>

              <p className="property-cta-note mb-0">
                Fast response, secure application, and clear owner communication in one flow.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>

      {/* Rental flow now handled by /apply/:propertyId page */}

      {/* Message Modal */}
      {showMessageModal && (
        <div 
          className="modal d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowMessageModal(false)}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Contact Owner</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowMessageModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6 className="fw-semibold">{property.name}</h6>
                  <p className="text-muted small mb-3">{property.state}, {property.city}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Your Message</label>
                  <textarea 
                    className="form-control"
                    rows="5"
                    placeholder="Write your message to the property owner..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  ></textarea>
                  <small className="text-muted">
                    Be respectful and include details about your inquiry.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowMessageModal(false)}
                  disabled={sendingMessage}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !message.trim()}
                >
                  {sendingMessage ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div 
          className="modal d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowReviewModal(false)}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Write a Review</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowReviewModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6 className="fw-semibold">{property.name}</h6>
                  <p className="text-muted small mb-3">{property.state}, {property.city}</p>
                </div>

                {userRentals.length > 1 && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Select Rental Period</label>
                    <select 
                      className="form-select"
                      value={selectedRentalId}
                      onChange={(e) => setSelectedRentalId(parseInt(e.target.value))}
                    >
                      {userRentals.map((rental) => (
                        <option key={rental.rental_id} value={rental.rental_id}>
                          {new Date(rental.start_date).toLocaleDateString()} - {new Date(rental.end_date).toLocaleDateString()}
                          {rental.status === 'active' ? ' (Current)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Rating</label>
                  <div className="d-flex gap-2 align-items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => setReviewRating(star)}
                        style={{ fontSize: '2rem', textDecoration: 'none' }}
                      >
                        <FontAwesomeIcon
                          icon={star <= reviewRating ? faStar : faStarRegular}
                          className={star <= reviewRating ? 'text-warning' : 'text-muted'}
                        />
                      </button>
                    ))}
                    <span className="ms-2">{reviewRating} star{reviewRating !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Your Review</label>
                  <textarea 
                    className="form-control"
                    rows="5"
                    placeholder="Share your experience with this property..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  ></textarea>
                  <small className="text-muted">
                    Help others by sharing your honest feedback.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowReviewModal(false)}
                  disabled={submittingReview}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleSubmitReview}
                  disabled={submittingReview || !reviewText.trim()}
                >
                  {submittingReview ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faStar} className="me-2" />
                      Submit Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {lightboxOpen && property?.photos && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          onMouseDown={handleLightboxMouseDown}
          onMouseMove={handleLightboxMouseMove}
          onMouseUp={handleLightboxMouseUp}
          onMouseLeave={handleLightboxMouseUp}
        >
          <button
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>

          <div
            className="lightbox-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              ref={lightboxImageRef}
              src={
                property.photos[lightboxImageIndex]
                  ? getPropertyImageUrl(property.photos[lightboxImageIndex], {
                      width: 2400,
                      quality: 95,
                      format: "jpeg",
                    })
                  : "https://via.placeholder.com/1200x800?text=No+Image"
              }
              alt={`${property.name || "Property"} - ${lightboxImageIndex + 1}`}
              className="lightbox-image"
              draggable={false}
              style={{
                transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                transformOrigin: "center",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
              onMouseDown={handleLightboxMouseDown}
            />
          </div>

          {property.photos.length > 1 && (
            <>
              <button
                className="lightbox-nav lightbox-nav-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevLightboxImage();
                }}
                aria-label="Previous image"
              >
                <FontAwesomeIcon icon={faChevronLeft} size="2x" />
              </button>
              <button
                className="lightbox-nav lightbox-nav-next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextLightboxImage();
                }}
                aria-label="Next image"
              >
                <FontAwesomeIcon icon={faChevronRight} size="2x" />
              </button>
            </>
          )}

          <div className="lightbox-info">
            <span className="image-info">
              {lightboxImageIndex + 1} / {property.photos.length}
            </span>
            <span className="zoom-info">
              Zoom: {Math.round(zoomLevel * 100)}%
              {zoomLevel > 1 && " • Drag to pan"}
            </span>
          </div>

          <div className="lightbox-instructions">
            <small>
              Scroll to zoom • Arrow keys to navigate • ESC to close
            </small>
          </div>
        </div>
      )}
    </>
  );
}

export default CardPopup;
